import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getContract, acceptContract, setupMandate, downloadContract, createUPISubscription, getMandateStatus } from "@/api";
import API from "@/api";
import { openRazorpaySubscription, loadRazorpayScript } from "@/utils/razorpay";
const submitBankDetails = (appId, data) => API.post(`/customer/bank-details/${appId}`, data);
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, FileText, Shield, CheckCircle, Download, Loader2, Landmark, CreditCard, Calculator, Smartphone, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ContractMandate() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [step, setStep] = useState("contract"); // contract | installment | mandate | processing | success | timeout | bank
  const [bankForm, setBankForm] = useState({ bank_account: "", ifsc: "" });
  const [selectedInstallment, setSelectedInstallment] = useState(1);
  const [loanAmount, setLoanAmount] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => { loadData(); }, [applicationId]);

  // Preload the Razorpay checkout script so it's ready when user clicks "Setup AutoPay"
  useEffect(() => {
    loadRazorpayScript().catch(() => {});
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const loadData = async () => {
    try {
      const res = await getContract(applicationId);
      setContract(res.data.contract);
      setMandate(res.data.mandate);

      const appLoanAmount = res.data.contract?.application?.loan_amount || 50000;
      setLoanAmount(appLoanAmount);

      if (res.data.contract?.accepted_by_user) {
        const m = res.data.mandate;
        if (m?.status === 'active' && m?.razorpay_subscription_id) {
          setStep("bank");
        } else if (m?.razorpay_subscription_id && m?.status === 'pending') {
          // Subscription created but not yet authenticated
          setStep("processing");
          startPolling(m.id);
        } else if (m?.installment_count && m?.per_installment_amount) {
          setStep("mandate");
        } else {
          setStep("installment");
        }
      }
    } catch {
      toast.error("Contract not found");
    } finally { setLoading(false); }
  };

  const handleDownload = async () => {
    try {
      const res = await downloadContract(applicationId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${applicationId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await acceptContract(applicationId);
      toast.success("Contract accepted!");
      setStep("installment");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  const handleInstallmentNext = async () => {
    // First setup mandate on backend to create EMI schedule
    setActionLoading(true);
    try {
      await setupMandate(applicationId, {
        mandate_type: 'upi_autopay',
        installment_count: selectedInstallment,
        loan_amount: loanAmount
      });
      await loadData();
      setStep("mandate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to setup installment plan");
    } finally {
      setActionLoading(false);
    }
  };

  const startPolling = useCallback((mandateId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await getMandateStatus(mandateId);
        const status = res.data.mandate?.status;
        console.log('[Autopay Poll] Status:', status);

        if (status === 'active') {
          clearInterval(pollRef.current);
          clearTimeout(timeoutRef.current);
          setMandate(res.data.mandate);
          setStep("success");
        }
      } catch (e) {
        console.error('[Autopay Poll] Error:', e);
      }
    }, 4000);

    // 3 minute timeout
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollRef.current);
      setStep("timeout");
    }, 180000);
  }, []);

  const handleSetupUPIAutopay = async () => {
    setActionLoading(true);
    try {
      if (!mandate?.id) {
        toast.error("Mandate not found. Please try again.");
        return;
      }

      // Step 1: Create subscription on backend
      const res = await createUPISubscription({
        user_id: user?.id,
        mandate_id: mandate.id
      });

      if (!res.data.success) {
        toast.error("Failed to create autopay subscription");
        return;
      }

      const data = res.data;
      setSubscriptionData(data);

      // Step 2 + 3: Open Razorpay subscription checkout via shared helper
      // (UPI Autopay display blocks ensure UPI shows at the top)
      await openRazorpaySubscription({
        subscriptionId: data.subscription_id,
        keyId: data.razorpay_key,
        description: "EMI Auto-Pay Setup",
        userMobile: user?.mobile || '',
        userName: user?.name || '',
        onSuccess: (response) => {
          console.log('[Razorpay Autopay] Setup success:', response);
          setStep('processing');
          startPolling(data.mandate_id);
        },
        onDismiss: () => {
          console.log('[Razorpay Autopay] Modal dismissed');
          toast.info("Autopay setup cancelled. You can try again.");
        },
        onFailure: (error) => {
          console.error('[Razorpay Autopay] Payment failed:', error);
          toast.error(error?.description || "Autopay setup failed");
        },
      });

    } catch (err) {
      console.error('[Autopay] Error:', err);
      toast.error(err.response?.data?.detail || "Failed to setup UPI Autopay");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckStatusAgain = async () => {
    if (!mandate?.id) return;
    setActionLoading(true);
    try {
      const res = await getMandateStatus(mandate.id);
      if (res.data.mandate?.status === 'active') {
        setMandate(res.data.mandate);
        setStep("success");
        toast.success("Auto-Pay is active!");
      } else {
        toast.info("Still pending. Please approve in your UPI app.");
        startPolling(mandate.id);
        setStep("processing");
      }
    } catch {
      toast.error("Failed to check status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    if (!bankForm.bank_account || !bankForm.ifsc) { toast.error("Fill all fields"); return; }
    setActionLoading(true);
    try {
      await submitBankDetails(applicationId, bankForm);
      toast.success("Bank details submitted! Disbursement is being processed.");
      navigate("/customer/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  // Calculate breakdown
  const processingFee = loanAmount * 0.02;
  const monthlyCharges = loanAmount * 0.03 * selectedInstallment;
  const totalRepayable = loanAmount + processingFee + monthlyCharges;
  const perInstallment = totalRepayable / selectedInstallment;

  // First EMI due date
  const firstDueDate = new Date();
  firstDueDate.setDate(firstDueDate.getDate() + 30);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#1565C0] border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contract-mandate-page">
      <header className="border-b border-slate-200 bg-white px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/customer/dashboard")} className="text-[#475569] hover:text-[#0F172A]">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="font-bold text-[#0F172A] tracking-tight" style={{fontFamily:'Outfit'}}>
            {step === "contract" ? "Contract Review" :
             step === "installment" ? "Choose Installments" :
             step === "mandate" ? "Set Up Auto-Pay" :
             step === "processing" ? "Setting Up Auto-Pay" :
             step === "success" ? "Auto-Pay Active" :
             step === "timeout" ? "Auto-Pay Pending" :
             "Bank Details"}
          </h1>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          {[
            { key: "contract", label: "Contract", icon: FileText },
            { key: "installment", label: "Plan", icon: Calculator },
            { key: "mandate", label: "Auto-Pay", icon: Smartphone },
            { key: "bank", label: "Bank", icon: Landmark },
          ].map((s, i) => {
            const isActive = step === s.key || (s.key === "mandate" && ["processing","success","timeout"].includes(step));
            const isDone = (s.key === "contract" && contract?.accepted_by_user) ||
                           (s.key === "installment" && mandate?.installment_count) ||
                           (s.key === "mandate" && (mandate?.status === "active" || step === "success" || step === "bank"));
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${isDone ? 'bg-[#10B981]/10 text-[#10B981]' : isActive ? 'bg-[#1565C0]/10 text-[#1565C0]' : 'bg-slate-100 text-[#475569]'}`}>
                  {isDone ? <CheckCircle className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < 3 && <div className="flex-1 h-px bg-slate-200 mx-1" />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        {/* Contract Step */}
        {step === "contract" && (
          <div className="bg-white border border-slate-200 rounded-md p-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#1565C0]/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#1565C0]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#0F172A]" style={{fontFamily:'Outfit'}}>EMI Assistance Contract</h2>
                <p className="text-xs text-[#475569]">Review and accept to proceed</p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4">
              <p className="text-sm text-[#475569] leading-relaxed">
                By accepting this contract, you authorize Rebuilt to act as your EMI assistance facilitator.
                We will communicate with your lender on your behalf for loan restructuring.
                The non-refundable subscription fee of INR 899 has been acknowledged.
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleDownload} variant="outline" data-testid="download-contract-btn" className="flex-1">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              <Button onClick={handleAccept} disabled={actionLoading} data-testid="accept-contract-btn" className="flex-1 bg-[#1565C0] hover:bg-[#0D47A1] text-white">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-2" /> Accept Contract</>}
              </Button>
            </div>
          </div>
        )}

        {/* Installment Selection Step */}
        {step === "installment" && (
          <div className="bg-white border border-slate-200 rounded-md p-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#1565C0]/10 flex items-center justify-center">
                <Calculator className="h-5 w-5 text-[#1565C0]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#0F172A]" style={{fontFamily:'Outfit'}}>Choose Installment Plan</h2>
                <p className="text-xs text-[#475569]">Select 1, 2, or 3 months repayment</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {[1, 2, 3].map(count => {
                const pFee = loanAmount * 0.02;
                const mCharges = loanAmount * 0.03 * count;
                const total = loanAmount + pFee + mCharges;
                const perInst = total / count;
                const isSelected = selectedInstallment === count;

                return (
                  <div
                    key={count}
                    onClick={() => setSelectedInstallment(count)}
                    className={`border-2 rounded-md p-4 cursor-pointer transition-all ${isSelected ? 'border-[#1565C0] bg-[#1565C0]/5' : 'border-slate-200 hover:border-[#1565C0]/50'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-[#0F172A]">{count} Month{count > 1 ? 's' : ''}</p>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#1565C0] bg-[#1565C0]' : 'border-slate-300'}`}>
                        {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-[#475569]">Loan Amount:</span><span className="font-medium">₹{loanAmount.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">Processing Fee (2%):</span><span>₹{pFee.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">Monthly Charges (3% × {count}mo):</span><span>₹{mCharges.toLocaleString()}</span></div>
                      <div className="flex justify-between pt-1 border-t border-slate-200"><span className="text-[#0F172A] font-medium">Total Repayable:</span><span className="font-bold text-[#1565C0]">₹{total.toLocaleString()}</span></div>
                      <div className="flex justify-between pt-1"><span className="text-[#0F172A] font-medium">Per Installment:</span><span className="font-bold text-[#10B981]">₹{perInst.toLocaleString()}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button onClick={handleInstallmentNext} disabled={actionLoading} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Continue to Auto-Pay Setup
            </Button>
          </div>
        )}

        {/* Mandate Step — UPI Autopay Setup */}
        {step === "mandate" && (
          <div className="bg-white border border-slate-200 rounded-md p-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#1565C0]/10 flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-[#1565C0]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#0F172A]" style={{fontFamily:'Outfit'}}>Set Up Auto-Pay for EMI</h2>
                <p className="text-xs text-[#475569]">UPI Autopay — automatic debit on due dates</p>
              </div>
            </div>

            {/* EMI Plan Summary */}
            <div className="bg-[#1565C0]/5 border border-[#1565C0]/20 rounded-lg p-4 mb-5">
              <p className="text-xs font-bold uppercase tracking-wider text-[#1565C0] mb-3">Your EMI Plan</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Per instalment</span>
                  <span className="font-black text-[#0F172A]" style={{fontFamily:'Outfit'}}>
                    ₹{Number(mandate?.per_installment_amount || perInstallment).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Number of instalments</span>
                  <span className="font-bold text-[#0F172A]">{mandate?.installment_count || selectedInstallment}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">First debit date</span>
                  <span className="font-bold text-[#0F172A]">
                    {firstDueDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#1565C0]/20">
                  <span className="text-[#475569] font-medium">Total amount</span>
                  <span className="font-black text-[#1565C0]" style={{fontFamily:'Outfit'}}>
                    ₹{Number(mandate?.total_repayable || totalRepayable).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-5">
              <p className="text-xs text-[#475569] leading-relaxed">
                Rebuilt will auto-debit your UPI on each due date.
                You will receive a reminder 3 days before each debit.
                You can also pay manually if autopay fails.
              </p>
            </div>

            <Button
              onClick={handleSetupUPIAutopay}
              disabled={actionLoading}
              data-testid="setup-autopay-btn"
              className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white py-5 text-base font-bold"
            >
              {actionLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Smartphone className="h-5 w-5 mr-2" />
              )}
              Set Up UPI Autopay
            </Button>

            <p className="text-center text-[10px] text-[#94A3B8] mt-3">
              Powered by Razorpay • Secure UPI Autopay
            </p>
          </div>
        )}

        {/* Processing Step */}
        {step === "processing" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 fade-in text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1565C0]/10 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-[#1565C0] animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{fontFamily:'Outfit'}}>Setting up your Auto-Pay...</h2>
            <p className="text-sm text-[#475569] mb-4 leading-relaxed">
              Please approve the UPI Autopay request<br />
              in your UPI app (Google Pay / PhonePe / Paytm)
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
              <Clock className="h-3.5 w-3.5" />
              Checking status...
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === "success" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 fade-in text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-[#10B981]" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{fontFamily:'Outfit'}}>Auto-Pay Activated!</h2>
            <p className="text-sm text-[#475569] mb-4 leading-relaxed">
              Your UPI Autopay is set up successfully.<br />
              EMIs will be automatically debited on due dates.
            </p>

            <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475569]">First debit</span>
                  <span className="font-bold text-[#0F172A]">
                    {mandate?.first_payment_date ? new Date(mandate.first_payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : firstDueDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475569]">Amount</span>
                  <span className="font-bold text-[#10B981]">₹{Number(mandate?.per_installment_amount || perInstallment).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setStep("bank")}
              className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white"
            >
              Continue to Bank Details
            </Button>
          </div>
        )}

        {/* Timeout Step */}
        {step === "timeout" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 fade-in text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{fontFamily:'Outfit'}}>Taking longer than expected</h2>
            <p className="text-sm text-[#475569] mb-6 leading-relaxed">
              Check your UPI app for a pending Autopay<br />
              request and approve it there.
            </p>

            <div className="flex gap-2">
              <Button
                onClick={handleCheckStatusAgain}
                disabled={actionLoading}
                variant="outline"
                className="flex-1"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check Status Again"}
              </Button>
              <Button
                onClick={() => navigate("/customer/dashboard")}
                className="flex-1 bg-[#1565C0] hover:bg-[#0D47A1] text-white"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Bank Details Step */}
        {step === "bank" && (
          <div className="bg-white border border-slate-200 rounded-md p-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#10B981]/10 flex items-center justify-center">
                <Landmark className="h-5 w-5 text-[#10B981]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#0F172A]" style={{fontFamily:'Outfit'}}>Bank Details for Disbursement</h2>
                <p className="text-xs text-[#475569]">Enter your bank account details</p>
              </div>
            </div>

            <form onSubmit={handleBankSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#475569]">Bank Account Number</Label>
                <Input data-testid="bank-account-input" value={bankForm.bank_account} onChange={e => setBankForm({...bankForm, bank_account: e.target.value})} placeholder="Enter account number" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#475569]">IFSC Code</Label>
                <Input data-testid="ifsc-input" value={bankForm.ifsc} onChange={e => setBankForm({...bankForm, ifsc: e.target.value.toUpperCase()})} placeholder="e.g. HDFC0001234" className="mt-1.5 uppercase" maxLength={11} />
              </div>
              <Button type="submit" disabled={actionLoading} data-testid="submit-bank-btn" className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white py-5">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit & Track Disbursement"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
